import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { CreatableSelect } from '@/components/ui/creatable-select';
import { toast } from 'sonner';
import apiClient from '@/utils/api';
import { useAuth } from '@/App';
import { COUNTRIES } from '@/utils/constants';
import { MoreVertical, Ban, Pause, CheckCircle, RotateCcw, Lock, Unlock, Edit, Trash2 } from 'lucide-react';
import GstBilling from './GstBilling';

// Stage configurations
// Universal 9-stage admission lifecycle - No skipping allowed
// Note: visa_approved and visa_denied are TWO OUTCOMES of the same "Visa Decision" stage
const STAGES = [
  { value: 'registration', label: 'Registration', color: 'bg-slate-100 text-slate-800', shortLabel: 'Reg' },
  { value: 'application_submitted', label: 'Application Submitted', color: 'bg-blue-100 text-blue-800', shortLabel: 'App' },
  { value: 'offer_letter_received', label: 'Offer Letter Received', color: 'bg-indigo-100 text-indigo-800', shortLabel: 'Offer' },
  { value: 'documentation_process', label: 'Documentation Process', color: 'bg-purple-100 text-purple-800', shortLabel: 'Docs' },
  { value: 'ministry_approval', label: 'Ministry Approval', color: 'bg-amber-100 text-amber-800', shortLabel: 'Ministry' },
  { value: 'visa_application_submitted', label: 'Visa Application Submitted', color: 'bg-cyan-100 text-cyan-800', shortLabel: 'Visa App' },
  { value: 'visa_decision', label: 'Visa Decision', color: 'bg-orange-100 text-orange-800', shortLabel: 'Decision', isVirtualStage: true }, // Virtual stage for display
  { value: 'ticket_travel_date', label: 'Ticket & Travel Date', color: 'bg-sky-100 text-sky-800', shortLabel: 'Ticket' },
  { value: 'student_flown', label: 'Student Flown', color: 'bg-teal-100 text-teal-800', shortLabel: 'Flown' }
];

// Actual backend stages (includes both visa outcomes)
const BACKEND_STAGES = [
  { value: 'registration', label: 'Registration', color: 'bg-slate-100 text-slate-800' },
  { value: 'application_submitted', label: 'Application Submitted', color: 'bg-blue-100 text-blue-800' },
  { value: 'offer_letter_received', label: 'Offer Letter Received', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'documentation_process', label: 'Documentation Process', color: 'bg-purple-100 text-purple-800' },
  { value: 'ministry_approval', label: 'Ministry Approval', color: 'bg-amber-100 text-amber-800' },
  { value: 'visa_application_submitted', label: 'Visa Application Submitted', color: 'bg-cyan-100 text-cyan-800' },
  { value: 'visa_approved', label: 'Visa Approved', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'visa_denied', label: 'Visa Denied', color: 'bg-red-100 text-red-800' },
  { value: 'ticket_travel_date', label: 'Ticket & Travel Date', color: 'bg-sky-100 text-sky-800' },
  { value: 'student_flown', label: 'Student Flown', color: 'bg-teal-100 text-teal-800' }
];

const DOCUMENT_TYPES = [
  { value: 'passport', label: 'Passport' },
  { value: 'academic_certificate', label: 'Academic Certificate' },
  { value: 'offer_letter', label: 'Offer Letter' },
  { value: 'visa_document', label: 'Visa Document' },
  { value: 'financial_document', label: 'Financial Document' },
  { value: 'identity_proof', label: 'Identity Proof' },
  { value: 'other', label: 'Other' }
];

const COMMUNICATION_TYPES = [
  { value: 'call', label: 'Phone Call' },
  { value: 'email', label: 'Email' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'receipt_shared', label: 'Receipt Shared' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'other', label: 'Other' }
];

export default function StudentPortal() {
  const { portalId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [portal, setPortal] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  
  // Programs list for course dropdown
  const [programs, setPrograms] = useState(['MBBS', 'BDS', 'Engineering', 'MBA', 'Nursing', 'Pharmacy']);
  const [universities, setUniversities] = useState([]);
  const canAddProgram = user && ['admin', 'counselor', 'team_leader', 'operating_head', 'documentation_team'].includes(user.role);
  
  // Documents state
  const [documents, setDocuments] = useState([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    document_name: '',
    description: '',
    share_with_student: false
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Activities & Communications
  const [activities, setActivities] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [commDialogOpen, setCommDialogOpen] = useState(false);
  const [commForm, setCommForm] = useState({
    communication_type: 'call',
    subject: '',
    notes: ''
  });
  
  // Student Messages
  const [studentMessages, setStudentMessages] = useState([]);
  const [staffPreview, setStaffPreview] = useState(null); // { doc, url, kind, loading }
  const [staffUpdates, setStaffUpdates] = useState([]);
  const [studentMsgReply, setStudentMsgReply] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [unreadMsgCount, setUnreadMsgCount] = useState(0);
  
  // Student Portal Account
  const [portalAccount, setPortalAccount] = useState(null);
  const [showResetCreds, setShowResetCreds] = useState(null);
  
  // Signed Agreements
  const [studentAgreements, setStudentAgreements] = useState([]);
  const [loadingAgreements, setLoadingAgreements] = useState(false);
  
  // Finance
  const [financeData, setFinanceData] = useState(null);
  const [portalSchedule, setPortalSchedule] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  
  // Raise Invoice
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({
    payment_type_name: 'Consultancy Fee',
    gross_amount: '',
    description: '',
    gst_type: 'inclusive'
  });
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  
  // Stage update
  const [stageDialogOpen, setStageDialogOpen] = useState(false);
  const [newStage, setNewStage] = useState('');
  const [stageNotes, setStageNotes] = useState('');

  // Status change (Cancel/Defer)
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusReason, setStatusReason] = useState('');
  const [deferToIntake, setDeferToIntake] = useState('');
  const [activeIntakes, setActiveIntakes] = useState([]);

  // Unlock dialog
  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false);
  const [unlockReason, setUnlockReason] = useState('');

  // Delete admission (admin only, for cancelled admissions)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Admission date edit (admin only)
  const [admDateDialogOpen, setAdmDateDialogOpen] = useState(false);
  const [admDateValue, setAdmDateValue] = useState('');
  const [admDateReason, setAdmDateReason] = useState('');
  const [admDateLoading, setAdmDateLoading] = useState(false);

  const isAdmin = user?.role === 'admin';

  // Edit profile
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    whatsapp_number: '',
    gender: '',
    university_name: '',
    course: '',
    intake: '',
    destination_country: ''
  });
  const [editLoading, setEditLoading] = useState(false);

  const fetchPortal = useCallback(async () => {
    try {
      const response = await apiClient.get(`/student-portal/${portalId}`);
      setPortal(response.data);
      // Fetch student portal account info
      try {
        const accRes = await apiClient.get(`/student-auth/account-info/${portalId}`);
        setPortalAccount(accRes.data);
      } catch { setPortalAccount(null); }
    } catch (error) {
      console.error('Error fetching portal:', error);
      toast.error('Failed to load student portal');
    } finally {
      setLoading(false);
    }
  }, [portalId]);

  const fetchDocuments = useCallback(async () => {
    try {
      const response = await apiClient.get(`/student-portal/${portalId}/documents`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  }, [portalId]);

  const fetchActivities = useCallback(async () => {
    try {
      const response = await apiClient.get(`/student-portal/${portalId}/activities`);
      setActivities(response.data);
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  }, [portalId]);

  const fetchCommunications = useCallback(async () => {
    try {
      const response = await apiClient.get(`/student-portal/${portalId}/communications`);
      setCommunications(response.data);
    } catch (error) {
      console.error('Error fetching communications:', error);
    }
  }, [portalId]);

  const fetchFinance = useCallback(async () => {
    try {
      const response = await apiClient.get(`/student-portal/${portalId}/finance`);
      setFinanceData(response.data);
    } catch (error) {
      console.error('Error fetching finance data:', error);
    }
  }, [portalId]);

  const fetchPortalSchedule = useCallback(async () => {
    setScheduleLoading(true);
    try {
      const [schedRes, agreeRes] = await Promise.all([
        apiClient.get(`/student-auth/portals/${portalId}/payment-schedule`),
        apiClient.get(`/student-auth/student-agreements/by-portal/${portalId}`).catch(() => ({ data: { agreements: [] } })),
      ]);
      const psAg = (agreeRes.data?.agreements || []).find(a => a.source === 'payment_schedule' && a.signed) ||
                   (agreeRes.data?.agreements || []).find(a => a.source === 'payment_schedule');
      setPortalSchedule({ ...schedRes.data, payment_schedule_agreement: psAg || null });
    } catch (error) {
      console.error('Error fetching portal payment schedule:', error);
      setPortalSchedule({ has_schedule: false, items: [], receipts: [] });
    } finally {
      setScheduleLoading(false);
    }
  }, [portalId]);

  const prefillInvoiceFromScheduleItem = (item) => {
    setInvoiceForm({
      payment_type_name: item.title || 'Consultancy Fee',
      gross_amount: String(item.amount_inr ? Math.round(item.amount_inr) : (item.amount || '')),
      description: item.description || `${item.title}${item.due_label ? ' · ' + item.due_label : ''}${item.currency && item.currency !== 'INR' ? ` (paid in ${item.currency})` : ''}`,
      gst_type: item.payee_type === 'indian_company' ? 'inclusive' : 'none',
    });
    setInvoiceDialogOpen(true);
  };

  const fetchStudentMessages = useCallback(async () => {
    try {
      const [msgRes, countRes, updRes] = await Promise.all([
        apiClient.get(`/student-portal/${portalId}/student-messages`),
        apiClient.get(`/student-portal/${portalId}/student-messages/unread-count`),
        apiClient.get(`/student-portal/${portalId}/updates`).catch(() => ({ data: { items: [] } })),
      ]);
      setStudentMessages(msgRes.data.messages || []);
      setUnreadMsgCount(updRes.data.unread_count ?? countRes.data.unread_count ?? 0);
      setStaffUpdates(updRes.data.items || []);
    } catch (error) {
      console.error('Error fetching student messages:', error);
    }
  }, [portalId]);

  const fetchStudentAgreements = useCallback(async () => {
    setLoadingAgreements(true);
    try {
      const res = await apiClient.get(`/student-auth/student-agreements/by-portal/${portalId}`);
      setStudentAgreements(res.data.agreements || []);
    } catch (error) {
      console.error('Error fetching agreements:', error);
    } finally {
      setLoadingAgreements(false);
    }
  }, [portalId]);

  const downloadSignedAgreement = async (agreement) => {
    try {
      const res = await apiClient.get(`/student-auth/student-agreements/${agreement.id}/download`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeTitle = (agreement.title || 'agreement').replace(/[^a-z0-9]/gi, '_').substring(0, 40);
      a.download = `${safeTitle}_signed.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download signed agreement');
    }
  };

  const fetchActiveIntakes = useCallback(async () => {
    try {
      // Fetch ALL intakes (active + upcoming) for lead assignment
      const response = await apiClient.get('/system-settings/intakes/all');
      setActiveIntakes(response.data.intakes || []);
    } catch (error) {
      console.error('Error fetching intakes:', error);
    }
  }, []);

  // Fetch programs from backend
  const fetchPrograms = useCallback(async () => {
    try {
      const response = await apiClient.get('/constants');
      if (response.data.programs) {
        setPrograms(response.data.programs);
      }
      if (response.data.universities) {
        setUniversities(response.data.universities);
      }
    } catch (error) {
      console.error('Error fetching programs:', error);
    }
  }, []);

  // Add new program to backend
  const handleAddProgram = async (programName) => {
    try {
      await apiClient.post('/constants/programs', { program: programName });
      await fetchPrograms();
      toast.success(`Program "${programName}" added successfully`);
    } catch (error) {
      console.error('Error adding program:', error);
      toast.error('Failed to add program');
    }
  };

  const handleAddUniversity = async (universityName) => {
    try {
      await apiClient.post('/constants/universities', { university: universityName });
      await fetchPrograms(); // This also fetches universities
      toast.success(`University "${universityName}" added successfully`);
    } catch (error) {
      console.error('Error adding university:', error);
      toast.error('Failed to add university');
    }
  };


  useEffect(() => {
    fetchPortal();
    fetchActiveIntakes();
    fetchPrograms();
  }, [fetchPortal, fetchActiveIntakes, fetchPrograms]);

  useEffect(() => {
    if (activeTab === 'documents') fetchDocuments();
    if (activeTab === 'activity') {
      fetchActivities();
      fetchCommunications();
    }
    if (activeTab === 'finance') { fetchFinance(); fetchPortalSchedule(); }
    if (activeTab === 'messages') {
      fetchStudentMessages();
      // Mark the staff-side Updates timeline as seen (clears the badge)
      setUnreadMsgCount(0);
      apiClient.post(`/student-portal/${portalId}/updates/mark-seen`).catch(() => {});
    }
    if (activeTab === 'info') fetchStudentAgreements();
  }, [activeTab, fetchDocuments, fetchActivities, fetchCommunications, fetchFinance, fetchPortalSchedule, fetchStudentMessages, fetchStudentAgreements]);

  // Fetch unread count on mount
  useEffect(() => {
    fetchStudentMessages();
  }, [fetchStudentMessages]);

  // Live badge: poll staff-side unread count every 60s (skips while on Updates tab)
  useEffect(() => {
    const timer = setInterval(async () => {
      if (activeTab === 'messages' || !portalId) return;
      try {
        const res = await apiClient.get(`/student-portal/${portalId}/updates/unread-count`);
        setUnreadMsgCount(res.data.unread_count || 0);
      } catch { /* non-fatal */ }
    }, 60000);
    return () => clearInterval(timer);
  }, [activeTab, portalId]);

  // Upload document
  const handleUpload = async () => {
    if (!selectedFile || !uploadForm.document_name) {
      toast.error('Please select a file and enter a name');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('document_type', 'other'); // Default type
    formData.append('document_name', uploadForm.document_name);
    if (uploadForm.description) formData.append('description', uploadForm.description);
    formData.append('share_with_student', uploadForm.share_with_student ? 'true' : 'false');
    
    try {
      await apiClient.post(`/student-portal/${portalId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(uploadForm.share_with_student ? 'Document uploaded & shared with student' : 'Document uploaded successfully');
      setUploadDialogOpen(false);
      setSelectedFile(null);
      setUploadForm({ document_name: '', description: '', share_with_student: false });
      fetchDocuments();
      fetchActivities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // Download document
  const handleDownload = async (docId, filename) => {
    try {
      const response = await apiClient.get(`/student-portal/${portalId}/documents/${docId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Download failed');
    }
  };

  // In-portal preview of a student/staff document (no download)
  const openStaffPreview = async (doc) => {
    setStaffPreview({ doc, url: null, kind: null, loading: true });
    try {
      const res = await apiClient.get(`/student-portal/${portalId}/documents/${doc.id}/preview`, { responseType: 'blob' });
      const blob = res.data;
      const type = (blob.type || res.headers['content-type'] || '').toLowerCase();
      const name = (doc.file_name || doc.original_filename || doc.document_name || '').toLowerCase();
      let kind = 'other';
      if (type.includes('pdf') || name.endsWith('.pdf')) kind = 'pdf';
      else if (type.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|heic|heif)$/.test(name)) kind = 'image';
      if (kind === 'other') {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = doc.file_name || doc.original_filename || 'document';
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        setStaffPreview(null);
        toast.info('This file type opens as a download');
        return;
      }
      const url = URL.createObjectURL(blob);
      setStaffPreview({ doc, url, kind, loading: false });
    } catch {
      setStaffPreview(null);
      toast.error('Failed to open document');
    }
  };

  const closeStaffPreview = () => {
    if (staffPreview?.url) URL.revokeObjectURL(staffPreview.url);
    setStaffPreview(null);
  };

  // Add communication
  const handleAddCommunication = async () => {
    if (!commForm.notes) {
      toast.error('Please enter notes');
      return;
    }
    
    try {
      await apiClient.post(`/student-portal/${portalId}/communications`, commForm);
      toast.success('Communication logged');
      setCommDialogOpen(false);
      setCommForm({ communication_type: 'call', subject: '', notes: '' });
      fetchCommunications();
    } catch (error) {
      toast.error('Failed to log communication');
    }
  };

  // Raise Invoice
  const handleRaiseInvoice = async () => {
    if (!invoiceForm.gross_amount) {
      toast.error('Please enter the invoice amount');
      return;
    }
    
    setInvoiceLoading(true);
    
    try {
      // Use lead_id if available, otherwise use portal id
      const studentId = portal.lead_id || portalId;
      
      const response = await apiClient.post('/finance/raise-invoice', null, {
        params: {
          student_id: studentId,
          student_name: portal.full_name || portal.student_name,
          payment_type_name: invoiceForm.payment_type_name,
          gross_amount: parseFloat(invoiceForm.gross_amount),
          description: invoiceForm.description || invoiceForm.payment_type_name,
          gst_type: invoiceForm.gst_type,
          admission_id: portalId
        }
      });
      
      toast.success(`Invoice raised successfully! Invoice No: ${response.data.invoice_number}`, {
        duration: 5000,
        action: {
          label: 'Download',
          onClick: () => handleDownloadFinanceDoc(response.data.payment_id, 'invoice')
        }
      });
      
      setInvoiceDialogOpen(false);
      setInvoiceForm({
        payment_type_name: 'Consultancy Fee',
        gross_amount: '',
        description: '',
        gst_type: 'inclusive'
      });
      
      // Refresh finance data
      fetchFinance();
      fetchActivities();
      fetchPortal();
    } catch (error) {
      console.error('Error raising invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to raise invoice');
    } finally {
      setInvoiceLoading(false);
    }
  };

  // Update stage
  const handleUpdateStage = async () => {
    if (!newStage) {
      toast.error('Please select a stage');
      return;
    }
    
    try {
      await apiClient.put(`/student-portal/${portalId}/stage`, {
        stage: newStage,
        notes: stageNotes
      });
      toast.success('Stage updated successfully');
      setStageDialogOpen(false);
      setNewStage('');
      setStageNotes('');
      fetchPortal();
      fetchActivities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update stage');
    }
  };

  // Update status (Cancel/Defer/Reactivate)
  const handleStatusChange = async (newStatus) => {
    try {
      const params = new URLSearchParams({ status: newStatus });
      if (statusReason) params.append('reason', statusReason);
      if (newStatus === 'deferred' && deferToIntake) {
        params.append('defer_to_intake', deferToIntake);
      }
      
      await apiClient.put(`/student-portal/${portalId}/status?${params.toString()}`);
      toast.success(
        newStatus === 'cancelled' ? 'Admission cancelled' :
        newStatus === 'deferred' ? `Admission deferred${deferToIntake ? ` to ${deferToIntake}` : ''}` :
        'Admission reactivated'
      );
      setStatusDialogOpen(false);
      setStatusReason('');
      setDeferToIntake('');
      fetchPortal();
      fetchActivities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  // Delete admission (Admin only, for cancelled admissions)
  const handleDeleteAdmission = async () => {
    try {
      setDeleteLoading(true);
      await apiClient.delete(`/student-portal/${portalId}`);
      toast.success('Admission record deleted successfully');
      setDeleteDialogOpen(false);
      navigate('/admissions');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete admission');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Download invoice/receipt from finance
  const handleDownloadFinanceDoc = async (paymentId, docType) => {
    try {
      toast.loading(`Downloading ${docType}...`);
      const response = await apiClient.get(`/finance/payments/${paymentId}/${docType}-pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${docType}_${paymentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.dismiss();
      toast.success(`${docType} downloaded`);
    } catch (error) {
      toast.dismiss();
      toast.error(`Failed to download ${docType}`);
    }
  };

  // Handle unlock profile (admin only)
  const handleUnlockProfile = async () => {
    if (!unlockReason.trim()) {
      toast.error('Please provide a reason for unlocking');
      return;
    }
    try {
      await apiClient.put(`/student-portal/${portalId}/unlock?reason=${encodeURIComponent(unlockReason)}`);
      toast.success('Profile unlocked successfully');
      setUnlockDialogOpen(false);
      setUnlockReason('');
      fetchPortal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock profile');
    }
  };

  // Handle admission date change (admin only)
  const handleOpenAdmDateDialog = () => {
    if (!portal?.created_at) return;
    const d = new Date(portal.created_at);
    const dateStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    setAdmDateValue(dateStr);
    setAdmDateReason('');
    setAdmDateDialogOpen(true);
  };

  const handleSaveAdmDate = async () => {
    if (!admDateValue) {
      toast.error('Please select a date');
      return;
    }
    if (!admDateReason.trim()) {
      toast.error('Please provide a reason for the date change');
      return;
    }
    setAdmDateLoading(true);
    try {
      await apiClient.put(`/student-portal/${portalId}/admission-date`, {
        admission_date: admDateValue,
        reason: admDateReason
      });
      toast.success('Admission date updated successfully');
      setAdmDateDialogOpen(false);
      fetchPortal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update admission date');
    } finally {
      setAdmDateLoading(false);
    }
  };

  // Handle edit profile
  const handleOpenEditDialog = () => {
    setEditForm({
      full_name: portal.full_name || '',
      email: portal.email || '',
      phone: portal.phone || '',
      whatsapp_number: portal.whatsapp_number || '',
      gender: portal.gender || '',
      university_name: portal.university_name || '',
      course: portal.course || '',
      intake: portal.intake || '',
      destination_country: portal.destination_country || ''
    });
    setEditDialogOpen(true);
  };

  const handleSaveProfile = async () => {
    if (!editForm.full_name.trim()) {
      toast.error('Full name is required');
      return;
    }
    
    setEditLoading(true);
    try {
      await apiClient.put(`/student-portal/${portalId}`, editForm);
      toast.success('Profile updated successfully');
      setEditDialogOpen(false);
      fetchPortal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setEditLoading(false);
    }
  };

  // Get stage config for backend stage values (includes visa_approved/denied)
  const getStageConfig = (stage) => {
    // Handle visa_approved and visa_denied as visa decision outcomes
    if (stage === 'visa_approved') {
      return { value: 'visa_approved', label: 'Visa Approved', color: 'bg-emerald-100 text-emerald-800' };
    }
    if (stage === 'visa_denied') {
      return { value: 'visa_denied', label: 'Visa Denied', color: 'bg-red-100 text-red-800' };
    }
    return BACKEND_STAGES.find(s => s.value === stage) || STAGES[0];
  };

  // Get display index for progress tracker (maps visa outcomes to visa_decision position)
  const getDisplayStageIndex = (stage) => {
    if (stage === 'visa_approved' || stage === 'visa_denied') {
      return 6; // visa_decision position in STAGES array
    }
    const idx = STAGES.findIndex(s => s.value === stage);
    return idx >= 0 ? idx : 0;
  };

  // Check if we're at a visa outcome stage
  const isAtVisaDenied = portal?.current_stage === 'visa_denied';
  const isAtVisaApproved = portal?.current_stage === 'visa_approved';
  const isAtVisaOutcome = isAtVisaDenied || isAtVisaApproved;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!portal) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Student portal not found</p>
        <Button onClick={() => navigate('/admissions')} className="mt-4">Back to Admissions</Button>
      </div>
    );
  }

  const stageConfig = getStageConfig(portal.current_stage);

  return (
    <div className="space-y-4 sm:space-y-6 p-3 sm:p-4 md:p-6 relative">
      {/* Admin-only Status Actions - Top Right Corner */}
      {(() => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const canChangeStatus = ['admin', 'operating_head', 'documentation_team'].includes(user.role);
        const canAdminAdmission = user.role === 'admin' || user.role === 'operating_head';
        if (!canChangeStatus) return null;
        return (
          <div className="absolute top-3 right-3 sm:top-4 sm:right-4 md:top-6 md:right-6 z-10">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" data-testid="status-menu-btn" className="h-8 w-8">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {portal.status === 'active' ? (
                  <>
                    <DropdownMenuItem 
                      onClick={() => setStatusDialogOpen(true)}
                      className="text-red-600 focus:text-red-600"
                      data-testid="cancel-menu-item"
                    >
                      <Ban className="mr-2 h-4 w-4" />
                      Cancel Admission
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => setStatusDialogOpen(true)}
                      className="text-amber-600 focus:text-amber-600"
                      data-testid="defer-menu-item"
                    >
                      <Pause className="mr-2 h-4 w-4" />
                      Defer to Another Intake
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {canAdminAdmission && (
                    <DropdownMenuItem
                      onClick={async () => {
                        try {
                          const res = await apiClient.post(`/student-auth/create-account?portal_id=${portal.id}`);
                          toast.success(
                            <div>
                              <p className="font-semibold">Student Account Created!</p>
                              <p className="text-xs mt-1">Login: {res.data.login_id}</p>
                              <p className="text-xs">Temp Password: {res.data.temp_password}</p>
                            </div>,
                            { duration: 15000 }
                          );
                        } catch (err) {
                          if (err.response?.status === 409) toast.info('Account already exists for this student');
                          else toast.error(err.response?.data?.detail || 'Failed to create account');
                        }
                      }}
                      data-testid="create-student-account-btn"
                    >
                      <i className="fas fa-user-graduate mr-2 text-sm"></i>
                      Create Student Portal Login
                    </DropdownMenuItem>
                    )}
                  </>
                ) : (
                  <>
                    <DropdownMenuItem 
                      onClick={() => handleStatusChange('active')}
                      className="text-green-600 focus:text-green-600"
                      data-testid="reactivate-menu-item"
                    >
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Reactivate Admission
                    </DropdownMenuItem>
                    {portal.status === 'cancelled' && canAdminAdmission && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={() => setDeleteDialogOpen(true)}
                          className="text-red-600 focus:text-red-600"
                          data-testid="delete-menu-item"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete Permanently
                        </DropdownMenuItem>
                      </>
                    )}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      })()}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 sm:gap-4 pr-10">
        <div>
          <Button variant="ghost" onClick={() => navigate('/admissions')} className="mb-1 sm:mb-2 h-8 text-xs sm:text-sm px-2 sm:px-3">
            <i className="fas fa-arrow-left mr-1 sm:mr-2"></i> 
            <span className="hidden sm:inline">Back to Admissions</span>
            <span className="sm:hidden">Back</span>
          </Button>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">{portal.full_name}</h1>
          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mt-1.5 sm:mt-2">
            <Badge variant="outline" className="text-xs sm:text-sm text-indigo-700 border-indigo-300 bg-indigo-50">
              {portal.student_id}
            </Badge>
            <Badge className={`text-xs sm:text-sm ${stageConfig.color}`}>
              {stageConfig.label}
            </Badge>
            <Badge variant={portal.status === 'active' ? 'default' : 'secondary'} className="text-xs sm:text-sm">
              {portal.status.toUpperCase()}
            </Badge>
            {portal.is_locked && (
              <Badge variant="destructive" className="bg-orange-500 hover:bg-orange-500 text-xs sm:text-sm">
                <Lock className="w-3 h-3 mr-1" />
                LOCKED
              </Badge>
            )}
          </div>
        </div>
        {portal.is_locked && (
          <Button 
            variant="outline"
            className="border-orange-300 text-orange-700 hover:bg-orange-50 h-8 sm:h-9 text-xs sm:text-sm"
            onClick={() => setUnlockDialogOpen(true)}
            data-testid="unlock-profile-btn"
          >
            <Unlock className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> 
            <span className="hidden sm:inline">Unlock Profile</span>
            <span className="sm:hidden">Unlock</span>
          </Button>
        )}
      </div>

      {/* Progress Tracker */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-gray-600">
            Admission Progress 
            {!portal.is_locked && (
              <span className="text-xs font-normal text-gray-400 ml-2">(click stage to update)</span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex overflow-x-auto pb-2">
            {STAGES.map((stage, idx) => {
              const currentDisplayIdx = getDisplayStageIndex(portal.current_stage);
              const isCompleted = idx < currentDisplayIdx || (idx === currentDisplayIdx && !isAtVisaDenied);
              const isCurrent = idx === currentDisplayIdx;
              const isVisaDecisionStage = stage.value === 'visa_decision';
              const isNextStage = idx === currentDisplayIdx + 1;
              
              // Determine if this stage is clickable
              // - Profile must not be locked
              // - Can click current stage to update, or next stage to advance
              // - If at visa_denied, clicking on visa_application_submitted is allowed (for resubmit)
              const canClickStage = !portal.is_locked && (
                isCurrent || 
                isNextStage || 
                (isAtVisaDenied && stage.value === 'visa_application_submitted')
              );
              
              // Special rendering for visa decision stage
              let stageLabel = stage.label;
              let stageBgClass = isCompleted ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-500';
              
              if (isVisaDecisionStage && isCurrent) {
                if (isAtVisaApproved) {
                  stageLabel = 'Visa Approved';
                  stageBgClass = 'bg-emerald-600 text-white ring-2 ring-emerald-300';
                } else if (isAtVisaDenied) {
                  stageLabel = 'Visa Denied';
                  stageBgClass = 'bg-red-600 text-white ring-2 ring-red-300';
                }
              }
              
              // Handle click on stage
              const handleStageClick = () => {
                if (!canClickStage) return;
                
                // Skip virtual visa_decision stage - handle it specially
                if (isVisaDecisionStage) {
                  // If current stage is visa_application_submitted, clicking visa_decision should open dialog
                  if (portal.current_stage === 'visa_application_submitted') {
                    setNewStage(''); // Will show the Approved/Denied buttons
                    setStageDialogOpen(true);
                  }
                  return;
                }
                
                // Set the target stage based on context
                if (isNextStage) {
                  // Clicking next stage to advance
                  setNewStage(stage.value);
                } else if (isCurrent) {
                  // Clicking current stage to see options
                  setNewStage(portal.current_stage);
                } else if (isAtVisaDenied && stage.value === 'visa_application_submitted') {
                  // Resubmit visa
                  setNewStage('visa_application_submitted');
                }
                setStageDialogOpen(true);
              };
              
              return (
                <div key={stage.value} className="flex items-center flex-shrink-0">
                  <div 
                    className={`flex flex-col items-center ${idx > 0 ? 'ml-2' : ''} ${canClickStage ? 'cursor-pointer group' : ''}`}
                    onClick={handleStageClick}
                    data-testid={`stage-${stage.value}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all ${stageBgClass} ${isCurrent && !isAtVisaDenied ? 'ring-2 ring-indigo-300' : ''} ${canClickStage ? 'group-hover:scale-110 group-hover:shadow-md' : ''}`}>
                      {isCompleted && !isAtVisaDenied ? <i className="fas fa-check"></i> : 
                       isAtVisaDenied && isVisaDecisionStage ? <i className="fas fa-times"></i> : 
                       idx + 1}
                    </div>
                    <span className={`text-[10px] mt-1 text-center max-w-[60px] transition-colors ${
                      isCurrent ? 'font-semibold text-indigo-700' : 'text-gray-500'
                    } ${isAtVisaDenied && isVisaDecisionStage ? 'text-red-600 font-semibold' : ''} ${canClickStage ? 'group-hover:text-indigo-600' : ''}`}>
                      {isVisaDecisionStage && isCurrent ? stageLabel : stage.label}
                    </span>
                  </div>
                  {idx < STAGES.length - 1 && (
                    <div className={`h-0.5 w-8 ${isCompleted && !isAtVisaDenied ? 'bg-indigo-600' : 'bg-gray-200'}`}></div>
                  )}
                </div>
              );
            })}
          </div>
          
          {/* Visa Denied Alert with Resubmit Option */}
          {isAtVisaDenied && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <i className="fas fa-exclamation-triangle text-red-500"></i>
                  <span className="text-sm text-red-700 font-medium">Visa application was denied</span>
                </div>
                <Button 
                  size="sm"
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-100"
                  onClick={() => {
                    setNewStage('visa_application_submitted');
                    setStageDialogOpen(true);
                  }}
                  data-testid="resubmit-visa-btn"
                >
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Resubmit Visa Application
                </Button>
              </div>
              <p className="text-xs text-red-600 mt-1">Click to move back to "Visa Application Submitted" stage for resubmission</p>
            </div>
          )}
          
          {/* Profile Locked Alert */}
          {portal.is_locked && (
            <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-orange-600" />
                  <span className="text-sm text-orange-700 font-medium">Profile is locked (Student Flown)</span>
                </div>
                <span className="text-xs text-orange-600">
                  Only administrators can edit this profile
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="flex w-full h-auto min-h-[36px] sm:min-h-[40px] flex-wrap gap-1 p-1">
          <TabsTrigger value="info" data-testid="tab-info" className="flex-1 min-w-[50px] text-xs sm:text-sm px-2 py-1.5 sm:px-3 sm:py-2">
            <i className="fas fa-user mr-1 hidden sm:inline"></i> 
            Info
          </TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents" className="flex-1 min-w-[50px] text-xs sm:text-sm px-2 py-1.5 sm:px-3 sm:py-2">
            <i className="fas fa-folder mr-1 hidden sm:inline"></i> 
            Docs
          </TabsTrigger>
          <TabsTrigger value="progress" data-testid="tab-progress" className="flex-1 min-w-[50px] text-xs sm:text-sm px-2 py-1.5 sm:px-3 sm:py-2">
            <i className="fas fa-tasks mr-1 hidden sm:inline"></i> 
            Prog
          </TabsTrigger>
          <TabsTrigger value="finance" data-testid="tab-finance" className="flex-1 min-w-[50px] text-xs sm:text-sm px-2 py-1.5 sm:px-3 sm:py-2">
            <i className="fas fa-rupee-sign mr-1 hidden sm:inline"></i> 
            <span className="hidden sm:inline">Finance</span>
            <span className="sm:hidden">₹</span>
          </TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity" className="flex-1 min-w-[50px] text-xs sm:text-sm px-2 py-1.5 sm:px-3 sm:py-2">
            <i className="fas fa-history mr-1 hidden sm:inline"></i> 
            Act
          </TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages" className="flex-1 min-w-[50px] text-xs sm:text-sm px-2 py-1.5 sm:px-3 sm:py-2 relative">
            <i className="fas fa-bell mr-1 hidden sm:inline"></i> 
            Updates
            {unreadMsgCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[8px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{unreadMsgCount}</span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Student Info Tab */}
        <TabsContent value="info" className="mt-3 sm:mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <Card>
              <CardHeader className="p-3 sm:p-4 flex flex-row items-center justify-between">
                <CardTitle className="text-base sm:text-lg">Personal Information</CardTitle>
                {!portal.is_locked && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleOpenEditDialog}
                    data-testid="edit-profile-btn"
                    className="h-8 text-xs"
                  >
                    <Edit className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                )}
              </CardHeader>
              <CardContent className="space-y-3 sm:space-y-4 p-3 sm:p-4 pt-0">
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label className="text-[10px] sm:text-xs text-gray-500">Full Name</Label>
                    <p className="font-medium text-sm sm:text-base">{portal.full_name}</p>
                  </div>
                  <div>
                    <Label className="text-[10px] sm:text-xs text-gray-500">Student ID</Label>
                    <p className="font-medium text-sm sm:text-base text-indigo-600">{portal.student_id}</p>
                  </div>
                  <div>
                    <Label className="text-[10px] sm:text-xs text-gray-500">Email</Label>
                    <p className="font-medium text-sm sm:text-base truncate">{portal.email || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-[10px] sm:text-xs text-gray-500">Phone</Label>
                    <p className="font-medium text-sm sm:text-base">{portal.phone || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-[10px] sm:text-xs text-gray-500">WhatsApp</Label>
                    <p className="font-medium text-sm sm:text-base">{portal.whatsapp_number || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-[10px] sm:text-xs text-gray-500">Gender</Label>
                    <p className="font-medium text-sm sm:text-base">{portal.gender || '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="p-3 sm:p-4">
                <CardTitle className="text-base sm:text-lg">Admission Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 sm:space-y-4 p-3 sm:p-4 pt-0">
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  {/* Admission Date - editable by admin */}
                  <div className="col-span-2 p-2.5 rounded-lg bg-indigo-50 border border-indigo-100">
                    <Label className="text-[10px] sm:text-xs text-indigo-600 font-medium">Admission Date</Label>
                    <div className="flex items-center justify-between mt-0.5">
                      <p className="font-semibold text-sm sm:text-base text-indigo-900">
                        {portal.created_at 
                          ? new Date(portal.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                          : '-'}
                      </p>
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleOpenAdmDateDialog}
                          className="h-7 px-2 text-xs text-indigo-600 hover:text-indigo-800 hover:bg-indigo-100"
                          data-testid="edit-admission-date-btn"
                        >
                          <Edit className="w-3 h-3 mr-1" />
                          Change
                        </Button>
                      )}
                    </div>
                    {portal.admission_date_modified && (
                      <p className="text-[9px] text-amber-600 mt-0.5">Date was modified by admin</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-[10px] sm:text-xs text-gray-500">University</Label>
                    <p className="font-medium text-sm sm:text-base">{portal.university_name || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-[10px] sm:text-xs text-gray-500">Course</Label>
                    <p className="font-medium text-sm sm:text-base">{portal.course || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-[10px] sm:text-xs text-gray-500">Intake</Label>
                    <p className="font-medium text-sm sm:text-base">{portal.intake || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-[10px] sm:text-xs text-gray-500">Destination</Label>
                    <p className="font-medium text-sm sm:text-base">{portal.destination_country || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-[10px] sm:text-xs text-gray-500">Counsellor</Label>
                    <p className="font-medium text-sm sm:text-base">{portal.assigned_counsellor_name || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-[10px] sm:text-xs text-gray-500">Team</Label>
                    <p className="font-medium text-sm sm:text-base">{portal.team_name || '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {portal.finance_summary && (
              <Card className="md:col-span-2">
                <CardHeader className="p-3 sm:p-4">
                  <CardTitle className="text-base sm:text-lg">Financial Summary</CardTitle>
                </CardHeader>
                <CardContent className="p-3 sm:p-4 pt-0">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
                    <div className="text-center p-2 sm:p-4 bg-blue-50 rounded-lg">
                      <p className="text-lg sm:text-2xl font-bold text-blue-700">₹{(portal.finance_summary.total_invoiced || 0).toLocaleString()}</p>
                      <p className="text-[10px] sm:text-xs text-gray-600">Total Invoiced</p>
                    </div>
                    <div className="text-center p-2 sm:p-4 bg-green-50 rounded-lg">
                      <p className="text-lg sm:text-2xl font-bold text-green-700">₹{(portal.finance_summary.total_received || 0).toLocaleString()}</p>
                      <p className="text-[10px] sm:text-xs text-gray-600">Total Received</p>
                    </div>
                    <div className="text-center p-2 sm:p-4 bg-red-50 rounded-lg">
                      <p className="text-lg sm:text-2xl font-bold text-red-700">₹{(portal.finance_summary.total_refunded || 0).toLocaleString()}</p>
                      <p className="text-[10px] sm:text-xs text-gray-600">Total Refunded</p>
                    </div>
                    <div className="text-center p-2 sm:p-4 bg-yellow-50 rounded-lg">
                      <p className="text-lg sm:text-2xl font-bold text-yellow-700">₹{(portal.finance_summary.balance_pending || 0).toLocaleString()}</p>
                      <p className="text-[10px] sm:text-xs text-gray-600">Balance Pending</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            {/* Portal Access Card */}
            <Card data-testid="portal-access-card">
              <CardHeader className="p-3 sm:p-4">
                <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                  <i className="fas fa-user-graduate text-indigo-500"></i>
                  Student Portal Access
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-4 pt-0">
                {!portalAccount?.has_account ? (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-500 mb-3">No portal account created yet</p>
                    <button
                      onClick={async () => {
                        try {
                          const res = await apiClient.post(`/student-auth/create-account?portal_id=${portal.id}`);
                          setShowResetCreds(res.data);
                          setPortalAccount({ has_account: true, login_id: res.data.login_id, is_active: true, must_change_password: true });
                          toast.success('Student portal account created!');
                        } catch (err) {
                          if (err.response?.status === 409) {
                            toast.info('Account already exists');
                            fetchPortal();
                          } else toast.error(err.response?.data?.detail || 'Failed to create account');
                        }
                      }}
                      className="px-4 py-2 bg-[#192650] text-white rounded-lg text-xs font-medium"
                      data-testid="create-portal-account-btn"
                    >
                      <i className="fas fa-plus mr-1.5"></i>Create Portal Account
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-[10px] text-gray-500 uppercase tracking-wide">Login ID</p>
                        <p className="text-sm font-medium text-gray-800 flex items-center gap-1.5" data-testid="portal-login-id">
                          {portalAccount.login_id}
                          <button onClick={() => { navigator.clipboard.writeText(portalAccount.login_id); toast.success('Copied!'); }} className="text-gray-400 hover:text-gray-600">
                            <i className="fas fa-copy text-[10px]"></i>
                          </button>
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-500 uppercase tracking-wide">Status</p>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${portalAccount.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {portalAccount.is_active ? 'Active' : 'Disabled'}
                        </span>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-500 uppercase tracking-wide">Last Login</p>
                        <p className="text-xs text-gray-700">{portalAccount.last_login ? new Date(portalAccount.last_login).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'Never'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-500 uppercase tracking-wide">Password</p>
                        <p className="text-xs text-gray-700">{portalAccount.must_change_password ? 'Temp (needs change)' : 'Set by student'}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button
                        onClick={async () => {
                          try {
                            const res = await apiClient.post(`/student-auth/reset-password/${portal.id}`);
                            setShowResetCreds(res.data);
                            setPortalAccount(prev => ({ ...prev, must_change_password: true }));
                            toast.success('Password reset! Share new credentials with the student.');
                          } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reset'); }
                        }}
                        className="px-3 py-1.5 border border-orange-300 text-orange-700 bg-orange-50 rounded-lg text-xs font-medium hover:bg-orange-100"
                        data-testid="reset-portal-password-btn"
                      >
                        <i className="fas fa-key mr-1.5"></i>Reset Password
                      </button>
                    </div>
                    
                    {/* Credentials Display */}
                    {showResetCreds && (
                      <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="credentials-display">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-semibold text-blue-800"><i className="fas fa-key mr-1.5"></i>Student Credentials</p>
                          <button onClick={() => setShowResetCreds(null)} className="text-blue-400 hover:text-blue-600 text-xs"><i className="fas fa-times"></i></button>
                        </div>
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between bg-white rounded px-2 py-1.5">
                            <span className="text-[10px] text-gray-500">Login:</span>
                            <span className="text-xs font-mono font-bold">{showResetCreds.login_id}</span>
                            <button onClick={() => { navigator.clipboard.writeText(showResetCreds.login_id); toast.success('Login copied!'); }} className="text-blue-500 text-[10px]"><i className="fas fa-copy"></i></button>
                          </div>
                          <div className="flex items-center justify-between bg-white rounded px-2 py-1.5">
                            <span className="text-[10px] text-gray-500">Password:</span>
                            <span className="text-xs font-mono font-bold text-orange-600">{showResetCreds.temp_password || showResetCreds.new_password}</span>
                            <button onClick={() => { navigator.clipboard.writeText(showResetCreds.temp_password || showResetCreds.new_password); toast.success('Password copied!'); }} className="text-blue-500 text-[10px]"><i className="fas fa-copy"></i></button>
                          </div>
                        </div>
                        <p className="text-[9px] text-blue-600 mt-2"><i className="fas fa-info-circle mr-1"></i>Student must change password on first login. Share these credentials securely.</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Signed Agreements Card */}
            <Card data-testid="student-agreements-card">
              <CardHeader className="flex flex-row items-center justify-between gap-2">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <i className="fas fa-file-signature text-indigo-600"></i>
                    Agreements & Consents
                  </CardTitle>
                  <p className="text-xs text-gray-500 mt-0.5">Signed agreements can be downloaded as audit-ready PDFs.</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchStudentAgreements}
                  disabled={loadingAgreements}
                  data-testid="refresh-agreements-btn"
                >
                  <i className={`fas ${loadingAgreements ? 'fa-spinner fa-spin' : 'fa-sync'} mr-1`}></i>Refresh
                </Button>
              </CardHeader>
              <CardContent>
                {loadingAgreements ? (
                  <div className="py-6 text-center text-sm text-gray-400">
                    <i className="fas fa-spinner fa-spin mr-2"></i>Loading...
                  </div>
                ) : studentAgreements.length === 0 ? (
                  <div className="py-8 text-center text-gray-400" data-testid="no-agreements-msg">
                    <i className="fas fa-file-contract text-3xl mb-2"></i>
                    <p className="text-xs">No agreements on record for this student yet.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {studentAgreements.map(ag => (
                      <div
                        key={ag.id}
                        className={`flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 rounded-lg border ${ag.signed ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}
                        data-testid={`admin-agreement-${ag.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h5 className="text-sm font-semibold text-gray-800">{ag.title}</h5>
                            {ag.signed ? (
                              <Badge variant="outline" className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-300">
                                <i className="fas fa-check mr-1"></i>Signed
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px] bg-amber-100 text-amber-700 border-amber-300">
                                <i className="fas fa-clock mr-1"></i>Pending
                              </Badge>
                            )}
                          </div>
                          {ag.signed && ag.signed_at && (
                            <p className="text-[11px] text-gray-600 mt-1">
                              Signed on {new Date(ag.signed_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              {ag.signature?.ip_address && ` · IP: ${ag.signature.ip_address}`}
                            </p>
                          )}
                        </div>
                        {ag.signed && (
                          <Button
                            onClick={() => downloadSignedAgreement(ag)}
                            variant="outline"
                            size="sm"
                            className="shrink-0"
                            data-testid={`download-signed-agreement-${ag.id}`}
                          >
                            <i className="fas fa-download mr-1.5"></i>Download PDF
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        <TabsContent value="documents" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-lg">Documents</CardTitle>
              <div className="flex gap-2">
                <Button onClick={() => setUploadDialogOpen(true)} data-testid="upload-document-btn" size="sm">
                  <i className="fas fa-upload mr-2"></i> Upload
                </Button>
                <Button variant="outline" size="sm" onClick={async () => {
                  const name = prompt('Document name to request from student:\n(e.g., Passport, Transcript, SOP)');
                  if (!name) return;
                  const instructions = prompt('Instructions or notes (optional):');
                  try {
                    await apiClient.post(`/student-portal/${portalId}/document-requests`, {
                      document_name: name,
                      instructions: instructions || ''
                    });
                    toast.success(`Requested "${name}" from student`);
                    fetchDocuments();
                  } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send request'); }
                }} data-testid="request-document-btn">
                  <i className="fas fa-paper-plane mr-2"></i> Request from Student
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Student-uploaded docs (pending review) */}
              {documents.filter(d => d.uploaded_by === 'student' && d.status === 'pending').length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-orange-700 mb-2 flex items-center gap-1.5">
                    <i className="fas fa-exclamation-circle"></i>
                    Pending Review ({documents.filter(d => d.uploaded_by === 'student' && d.status === 'pending').length})
                  </h4>
                  <div className="space-y-2">
                    {documents.filter(d => d.uploaded_by === 'student' && d.status === 'pending').map(doc => (
                      <div key={doc.id} className="flex items-center justify-between p-3 border-2 border-orange-200 bg-orange-50 rounded-lg" data-testid={`review-doc-${doc.id}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center">
                            <i className="fas fa-file-alt text-orange-600 text-sm"></i>
                          </div>
                          <div>
                            <p className="text-sm font-medium">{doc.document_name || doc.document_type}</p>
                            <p className="text-[10px] text-gray-500">{doc.file_name} | Uploaded by student | {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : ''}</p>
                          </div>
                        </div>
                        <div className="flex gap-1.5 flex-shrink-0">
                          <Button size="sm" variant="outline" className="h-7 text-xs bg-[#192650] text-white hover:bg-[#243466]" onClick={() => openStaffPreview(doc)} data-testid={`preview-doc-${doc.id}`}>
                            <i className="fas fa-eye mr-1"></i>View
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={async () => {
                            try {
                              const res = await apiClient.get(`/student-portal/${portalId}/documents/${doc.id}/preview`, { responseType: 'blob' });
                              const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/octet-stream' });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = doc.file_name || 'document';
                              a.click();
                              URL.revokeObjectURL(url);
                            } catch (err) { toast.error('Failed to download document'); }
                          }} data-testid={`download-doc-${doc.id}`}>
                            <i className="fas fa-download mr-1"></i>Download
                          </Button>
                          <Button size="sm" className="h-7 bg-green-600 hover:bg-green-700 text-xs" onClick={async () => {
                            try {
                              await apiClient.put(`/student-portal/${portalId}/documents/${doc.id}/review`, { action: 'approve' });
                              toast.success('Document approved');
                              fetchDocuments();
                            } catch (err) { toast.error('Failed to approve'); }
                          }} data-testid={`approve-doc-${doc.id}`}>
                            <i className="fas fa-check mr-1"></i>Approve
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs border-red-300 text-red-600 hover:bg-red-50" onClick={async () => {
                            const reason = prompt('Reason for rejection:');
                            if (reason === null) return;
                            try {
                              await apiClient.put(`/student-portal/${portalId}/documents/${doc.id}/review`, { action: 'reject', reason });
                              toast.success('Document rejected');
                              fetchDocuments();
                            } catch (err) { toast.error('Failed to reject'); }
                          }} data-testid={`reject-doc-${doc.id}`}>
                            <i className="fas fa-times mr-1"></i>Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* All documents */}
              {documents.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <i className="fas fa-folder-open text-4xl mb-4"></i>
                  <p>No documents yet. Upload or request from student.</p>
                </div>
              ) : (
                <div>
                  {documents.filter(d => !(d.uploaded_by === 'student' && d.status === 'pending')).length > 0 && (
                    <h4 className="text-xs font-semibold text-gray-500 mb-2">All Documents</h4>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {documents.filter(d => !(d.uploaded_by === 'student' && d.status === 'pending')).map((doc) => (
                      <div key={doc.id} className="border rounded-lg p-3 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                              doc.status === 'approved' ? 'bg-green-100 text-green-600' :
                              doc.status === 'rejected' ? 'bg-red-100 text-red-600' :
                              doc.uploaded_by === 'student' ? 'bg-blue-100 text-blue-600' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              <i className={`fas ${doc.status === 'approved' ? 'fa-check-circle' : doc.status === 'rejected' ? 'fa-times-circle' : 'fa-file'}`}></i>
                            </div>
                            <div>
                              <p className="font-medium text-xs">{doc.document_name || doc.document_type || doc.file_name}</p>
                              <p className="text-[10px] text-gray-500">
                                {doc.uploaded_by === 'student' ? 'Student upload' : 'Staff upload'}
                                {doc.status && <span className={`ml-1 font-semibold ${doc.status === 'approved' ? 'text-green-600' : doc.status === 'rejected' ? 'text-red-600' : 'text-amber-600'}`}> | {doc.status}</span>}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="mt-2 flex items-center justify-between text-[10px] text-gray-400">
                          <span>{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('en-IN') : ''}</span>
                          {doc.file_size && <span>{(doc.file_size / 1024).toFixed(1)} KB</span>}
                        </div>
                        {(doc.gridfs_id || doc.gridfs_file_id || doc.original_filename) ? (
                          <div className="mt-2 flex gap-1.5">
                            <Button size="sm" className="flex-1 h-7 text-xs bg-[#192650] hover:bg-[#243466]" onClick={() => openStaffPreview(doc)} data-testid={`preview-doc-${doc.id}`}>
                              <i className="fas fa-eye mr-1"></i>View
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs px-2.5" onClick={async () => {
                            try {
                              const endpoint = (doc.gridfs_id || doc.gridfs_file_id) 
                                ? `/student-portal/${portalId}/documents/${doc.id}/preview` 
                                : `/student-portal/${portalId}/documents/${doc.id}/download`;
                              const res = await apiClient.get(endpoint, { responseType: 'blob' });
                              const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/octet-stream' });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = doc.file_name || doc.original_filename || 'document';
                              a.click();
                              URL.revokeObjectURL(url);
                            } catch { toast.error('Failed to download'); }
                          }} data-testid={`download-doc-all-${doc.id}`}>
                            <i className="fas fa-download"></i>
                          </Button>
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Progress Tab */}
        <TabsContent value="progress" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Stage History</CardTitle>
            </CardHeader>
            <CardContent>
              {portal.stage_history && portal.stage_history.length > 0 ? (
                <div className="space-y-4">
                  {portal.stage_history.map((stage, idx) => {
                    const config = getStageConfig(stage.stage);
                    const isDenied = stage.stage === 'visa_denied';
                    const isResubmission = stage.stage === 'visa_application_submitted' && 
                      portal.stage_history.slice(0, idx).some(s => s.stage === 'visa_denied');
                    return (
                      <div key={stage.id || idx} className="flex items-start gap-4 pb-4 border-b last:border-0">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${config.color}`}>
                          <i className={`fas ${isDenied ? 'fa-times' : isResubmission ? 'fa-redo' : 'fa-check'} text-sm`}></i>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{config.label}</p>
                            {isResubmission && (
                              <Badge variant="outline" className="text-xs">Resubmission</Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">
                            {stage.completed_at ? new Date(stage.completed_at).toLocaleString() : '-'}
                          </p>
                          {stage.completed_by_name && (
                            <p className="text-xs text-gray-400">by {stage.completed_by_name}</p>
                          )}
                          {stage.notes && (
                            <p className="text-sm mt-1 text-gray-600 bg-gray-50 p-2 rounded">{stage.notes}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center py-8 text-gray-500">No stage history available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Finance Tab */}
        <TabsContent value="finance" className="mt-3 sm:mt-4 space-y-3 sm:space-y-4">
          {/* Payment Schedule (country + program) — reference for finance team */}
          <Card data-testid="admin-payment-schedule-card">
            <CardHeader className="p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                    <i className="fas fa-map-signs text-emerald-600 shrink-0"></i>
                    <span>Applicable Payment Schedule</span>
                  </CardTitle>
                  <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">
                    {portalSchedule?.has_schedule ? (
                      <>For <strong>{portalSchedule.country}</strong> · <strong>{portalSchedule.program}</strong> — tap a row to create an invoice.</>
                    ) : (
                      <>No schedule published yet for this student's country/program.</>
                    )}
                  </p>
                </div>
                {portalSchedule?.has_schedule && (
                  portalSchedule.payment_schedule_agreement?.signed ? (
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-emerald-100 text-emerald-700 text-[10px] sm:text-[11px] font-semibold whitespace-nowrap self-start sm:self-auto" data-testid="ps-signed-badge">
                      <i className="fas fa-check-circle"></i>
                      <span>Acknowledged{portalSchedule.payment_schedule_agreement.signed_at ? ` · ${new Date(portalSchedule.payment_schedule_agreement.signed_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}` : ''}</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-amber-100 text-amber-700 text-[10px] sm:text-[11px] font-semibold whitespace-nowrap self-start sm:self-auto" data-testid="ps-unsigned-badge">
                      <i className="fas fa-hourglass-half"></i>
                      <span>Pending acknowledgement</span>
                    </span>
                  )
                )}
              </div>
            </CardHeader>
            <CardContent className="p-3 sm:p-4 pt-0">
              {scheduleLoading ? (
                <div className="py-6 text-center text-gray-400 text-sm"><i className="fas fa-spinner fa-spin mr-2"></i>Loading schedule...</div>
              ) : !portalSchedule?.has_schedule ? (
                <div className="py-6 text-center border-2 border-dashed rounded-lg bg-gray-50" data-testid="no-schedule-admin">
                  <i className="fas fa-clock text-xl text-gray-300 mb-2"></i>
                  <p className="text-xs text-gray-500 max-w-md mx-auto">
                    This student's country/program combination does not have a payment schedule yet. Create one in <strong>Settings → Payment Schedules</strong>.
                  </p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {portalSchedule.items.map((it, idx) => {
                    const currencySymbols = { INR: '₹', USD: '$', EUR: '€', GBP: '£', AUD: 'A$', CAD: 'C$' };
                    const itemCurrency = (it.currency || 'INR').toUpperCase();
                    const itemSym = currencySymbols[itemCurrency] || itemCurrency + ' ';
                    const showInrEq = itemCurrency !== 'INR' && (Number(it.amount_inr) > 0);
                    const payeeLabels = {
                      indian_company: 'Indian Co.',
                      university: 'University',
                      hostel: 'Hostel',
                      other: 'Other',
                      company: 'Indian Co.',
                      institution: 'Other',
                    };
                    const statusStyle = {
                      paid: 'bg-emerald-50 border-emerald-200',
                      invoiced: 'bg-amber-50 border-amber-200',
                      planned: 'bg-gray-50 border-gray-200',
                    }[it.status] || 'bg-gray-50 border-gray-200';
                    const isPaid = it.status === 'paid';
                    const isInvoiced = it.status === 'invoiced';
                    return (
                      <div key={it.id || idx} className={`rounded-lg border px-2.5 py-2 ${statusStyle}`} data-testid={`admin-schedule-item-${idx}`}>
                        {/* Top row: # + title + amount + button */}
                        <div className="flex items-center gap-2">
                          <div className="shrink-0 w-5 h-5 rounded-full bg-white border flex items-center justify-center text-[10px] font-bold text-gray-700">
                            {idx + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] sm:text-sm font-semibold text-gray-800 truncate leading-tight" title={it.title}>{it.title}</p>
                          </div>
                          <div className="shrink-0 text-right leading-tight">
                            <div className="text-[13px] sm:text-sm font-bold text-gray-900">{itemSym}{Number(it.amount || 0).toLocaleString('en-IN')}</div>
                            {showInrEq && <div className="text-[10px] text-gray-500">≈ ₹{Number(it.amount_inr).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>}
                          </div>
                          {!isPaid && (
                            <Button
                              size="sm"
                              onClick={() => prefillInvoiceFromScheduleItem(it)}
                              className="bg-indigo-600 hover:bg-indigo-700 h-7 text-[11px] px-2 shrink-0"
                              data-testid={`prefill-invoice-btn-${idx}`}
                              title="Create Invoice"
                            >
                              <i className="fas fa-file-invoice sm:mr-1"></i>
                              <span className="hidden sm:inline">Invoice</span>
                            </Button>
                          )}
                        </div>
                        {/* Meta row: due, pay-to, status — small, single line on mobile */}
                        <div className="mt-1 ml-7 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-gray-600">
                          {it.due_label && (
                            <span className="inline-flex items-center gap-1">
                              <i className="far fa-calendar text-gray-400"></i>{it.due_label}
                            </span>
                          )}
                          <span className="inline-flex items-center gap-1">
                            <i className="fas fa-university text-gray-400"></i>{payeeLabels[it.payee_type] || 'Other'}
                          </span>
                          {isPaid && <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">Paid · {it.receipt_number}</span>}
                          {isInvoiced && <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">Invoiced</span>}
                        </div>
                      </div>
                    );
                  })}
                  <div className="pt-2 flex items-center justify-between text-xs">
                    <span className="text-gray-500">Total initial fees (INR equivalent)</span>
                    <span className="font-bold text-emerald-700">₹{Number(portalSchedule.total_inr || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                  </div>
                  <div className="rounded bg-blue-50 border border-blue-200 p-2 text-[10px] sm:text-[11px] text-blue-800 flex items-start gap-1.5">
                    <i className="fas fa-info-circle text-blue-600 mt-0.5 shrink-0"></i>
                    <span>Indian Company Account payments attract <strong>18% GST</strong>. Foreign-currency totals use today's FX rate; use the invoice date's rate when raising invoices.</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="p-3 sm:p-4">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-base sm:text-lg">Finance Overview</CardTitle>
                <Button 
                  onClick={() => setInvoiceDialogOpen(true)}
                  className="bg-indigo-600 hover:bg-indigo-700 h-8 text-xs sm:text-sm shrink-0"
                  data-testid="raise-invoice-btn"
                >
                  <i className="fas fa-file-invoice mr-1 sm:mr-2"></i>
                  <span className="hidden sm:inline">Raise Invoice</span>
                  <span className="sm:hidden">Invoice</span>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-3 sm:p-4 pt-0">
              {/* GST / company billing — applies to all invoices for this student */}
              <div className="mb-3 sm:mb-4 pb-3 sm:pb-4 border-b">
                <p className="text-xs text-gray-500 mb-2">Bill invoices to a GST-registered company/parent (optional)</p>
                <GstBilling studentId={portal?.lead_id || portalId} />
              </div>
              {financeData ? (
                <div className="space-y-4 sm:space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
                    <div className="text-center p-2 sm:p-4 bg-blue-50 rounded-lg">
                      <p className="text-base sm:text-xl font-bold text-blue-700">₹{(financeData.summary.total_invoiced || 0).toLocaleString()}</p>
                      <p className="text-[10px] sm:text-xs text-gray-600">Invoiced</p>
                    </div>
                    <div className="text-center p-2 sm:p-4 bg-green-50 rounded-lg">
                      <p className="text-base sm:text-xl font-bold text-green-700">₹{(financeData.summary.total_received || 0).toLocaleString()}</p>
                      <p className="text-[10px] sm:text-xs text-gray-600">Received</p>
                    </div>
                    <div className="text-center p-2 sm:p-4 bg-red-50 rounded-lg">
                      <p className="text-base sm:text-xl font-bold text-red-700">₹{(financeData.summary.total_refunded || 0).toLocaleString()}</p>
                      <p className="text-[10px] sm:text-xs text-gray-600">Refunded</p>
                    </div>
                    <div className="text-center p-2 sm:p-4 bg-yellow-50 rounded-lg">
                      <p className="text-base sm:text-xl font-bold text-yellow-700">₹{(financeData.summary.balance_due || 0).toLocaleString()}</p>
                      <p className="text-[10px] sm:text-xs text-gray-600">Balance</p>
                    </div>
                  </div>

                  {/* Payment List */}
                  <div>
                    <h3 className="font-medium mb-3">Payment Records</h3>
                    {financeData.payments.length === 0 ? (
                      <p className="text-center py-8 text-gray-500">No payment records</p>
                    ) : (
                      <div className="space-y-3">
                        {financeData.payments.map((payment) => {
                          const bankReceipts = (portalSchedule?.receipts || []).filter(r => r.invoice_id === payment.id);
                          return (
                          <div key={payment.id} className="border rounded-lg p-4">
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="font-medium">{payment.payment_type_name || payment.description}</p>
                                <p className="text-sm text-gray-500">{payment.invoice_number}</p>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-lg">₹{payment.net_amount?.toLocaleString()}</p>
                                <Badge className={
                                  payment.status === 'received' ? 'bg-green-100 text-green-800' :
                                  payment.status === 'refunded' ? 'bg-red-100 text-red-800' :
                                  payment.status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }>
                                  {payment.status}
                                </Badge>
                              </div>
                            </div>
                            
                            {/* GST Breakdown */}
                            {payment.total_gst > 0 && (
                              <div className="mt-2 text-xs text-gray-500 flex gap-4">
                                <span>Base: ₹{payment.base_amount?.toLocaleString()}</span>
                                <span>CGST: ₹{payment.cgst_amount?.toLocaleString()}</span>
                                <span>SGST: ₹{payment.sgst_amount?.toLocaleString()}</span>
                              </div>
                            )}
                            
                            {/* Document Download Buttons */}
                            <div className="mt-3 flex gap-2">
                              {payment.invoice_number && (
                                <Button size="sm" variant="outline" onClick={() => handleDownloadFinanceDoc(payment.id, 'invoice')}>
                                  <i className="fas fa-file-invoice mr-1"></i> Invoice
                                </Button>
                              )}
                              {payment.status === 'received' && payment.receipt_number && (
                                <Button size="sm" variant="outline" onClick={() => handleDownloadFinanceDoc(payment.id, 'receipt')}>
                                  <i className="fas fa-receipt mr-1"></i> Receipt
                                </Button>
                              )}
                              {payment.status === 'refunded' && payment.credit_note_number && (
                                <Button size="sm" variant="outline" onClick={() => handleDownloadFinanceDoc(payment.id, 'credit-note')}>
                                  <i className="fas fa-file-alt mr-1"></i> Credit Note
                                </Button>
                              )}
                            </div>

                            {/* Student-uploaded bank receipts (offline payments) */}
                            {bankReceipts.length > 0 && (
                              <div className="mt-3 rounded-lg border border-indigo-100 bg-indigo-50/50 p-2" data-testid={`bank-receipts-${payment.id}`}>
                                <div className="text-[11px] font-semibold text-indigo-700 flex items-center gap-1.5 mb-1.5">
                                  <i className="fas fa-receipt"></i> Bank Receipt uploaded by student
                                </div>
                                <div className="space-y-1">
                                  {bankReceipts.map(r => (
                                    <div key={r.id} className="flex flex-wrap items-center gap-2 text-xs bg-white rounded border border-gray-200 px-2 py-1.5">
                                      <i className={`fas ${r.content_type === 'application/pdf' ? 'fa-file-pdf text-red-500' : 'fa-file-image text-blue-500'}`}></i>
                                      <button
                                        onClick={() => { apiClient.get(`/student-auth/payment-receipts/${r.id}/download`, { responseType: 'blob' }).then(res => { const url = URL.createObjectURL(res.data); window.open(url, '_blank'); }).catch(() => toast.error('Failed to open receipt')); }}
                                        className="text-indigo-600 hover:underline truncate max-w-[200px]"
                                        data-testid={`admin-open-receipt-${r.id}`}
                                      >
                                        {r.file_name}
                                      </button>
                                      <span className="text-gray-400 text-[10px]">{new Date(r.uploaded_at).toLocaleDateString()}</span>
                                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                                        r.status === 'verified' ? 'bg-emerald-100 text-emerald-700' :
                                        r.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                                      }`}>
                                        {r.status || 'pending'}
                                      </span>
                                      {r.status === 'pending' && (
                                        <div className="ml-auto flex gap-1.5">
                                          <button
                                            onClick={async () => {
                                              if (!window.confirm('Verify this receipt and auto-mark the invoice as PAID?')) return;
                                              try {
                                                const res = await apiClient.post(`/student-auth/payment-receipts/${r.id}/verify`, { note: '' });
                                                toast.success(`Invoice paid · Receipt ${res.data.receipt_number || ''}`);
                                                fetchPortalSchedule();
                                                fetchFinance();
                                              } catch (err) {
                                                toast.error(err.response?.data?.detail || 'Failed to verify');
                                              }
                                            }}
                                            className="h-6 px-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-semibold"
                                            data-testid={`verify-receipt-${r.id}`}
                                          >
                                            <i className="fas fa-check mr-1"></i>Verify &amp; Mark Paid
                                          </button>
                                          <button
                                            onClick={async () => {
                                              const reason = window.prompt('Reason for rejecting this receipt? (Shared with the student)');
                                              if (!reason || !reason.trim()) return;
                                              try {
                                                await apiClient.post(`/student-auth/payment-receipts/${r.id}/reject`, { reason: reason.trim() });
                                                toast.success('Receipt rejected. Student notified.');
                                                fetchPortalSchedule();
                                                fetchFinance();
                                              } catch (err) {
                                                toast.error(err.response?.data?.detail || 'Failed to reject');
                                              }
                                            }}
                                            className="h-6 px-2 rounded bg-white border border-red-300 hover:bg-red-50 text-red-700 text-[10px] font-semibold"
                                            data-testid={`reject-receipt-${r.id}`}
                                          >
                                            <i className="fas fa-times mr-1"></i>Reject
                                          </button>
                                        </div>
                                      )}
                                      {r.status === 'verified' && r.verified_by_name && (
                                        <span className="ml-auto text-[10px] text-emerald-700">✓ {r.verified_by_name}</span>
                                      )}
                                      {r.status === 'rejected' && r.rejection_reason && (
                                        <span className="w-full text-[10px] text-red-700 mt-1 pl-4">Reason: {r.rejection_reason}</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                  <p className="mt-2 text-gray-500">Loading finance data...</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Activity Timeline */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Activity Timeline</CardTitle>
              </CardHeader>
              <CardContent>
                {activities.length === 0 ? (
                  <p className="text-center py-8 text-gray-500">No activities yet</p>
                ) : (
                  <div className="space-y-4 max-h-[500px] overflow-y-auto">
                    {activities.map((activity) => {
                      const isLeadStage = activity.stage === 'lead';
                      return (
                        <div key={activity.id} className="flex gap-3 pb-4 border-b last:border-0">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            isLeadStage ? 'bg-amber-100 text-amber-600' : 'bg-indigo-100 text-indigo-600'
                          }`}>
                            <i className={`fas text-xs ${
                              (activity.activity_type || '').includes('document') ? 'fa-file' :
                              (activity.activity_type || '').includes('payment') ? 'fa-rupee-sign' :
                              (activity.activity_type || '').includes('stage') ? 'fa-tasks' :
                              (activity.activity_type || '').includes('call') ? 'fa-phone' :
                              (activity.activity_type || '').includes('lead_created') ? 'fa-user-plus' :
                              isLeadStage ? 'fa-bolt' : 'fa-info'
                            }`}></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-sm">{activity.title}</p>
                              {isLeadStage && (
                                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-medium uppercase tracking-wide">
                                  Lead Stage
                                </span>
                              )}
                            </div>
                            {activity.description && (
                              <p className="text-xs text-gray-500 whitespace-pre-wrap">{activity.description}</p>
                            )}
                            <p className="text-xs text-gray-400 mt-1">
                              {new Date(activity.created_at).toLocaleString()}
                              {activity.created_by_name && ` • ${activity.created_by_name}`}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Communication Log */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Communication Log</CardTitle>
                <Button size="sm" onClick={() => setCommDialogOpen(true)} data-testid="add-communication-btn">
                  <i className="fas fa-plus mr-2"></i> Add
                </Button>
              </CardHeader>
              <CardContent>
                {communications.length === 0 ? (
                  <p className="text-center py-8 text-gray-500">No communications logged</p>
                ) : (
                  <div className="space-y-4 max-h-[500px] overflow-y-auto">
                    {communications.map((comm) => (
                      <div key={comm.id} className="border rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="text-xs">
                            {COMMUNICATION_TYPES.find(t => t.value === comm.communication_type)?.label || comm.communication_type}
                          </Badge>
                          {comm.subject && <span className="text-sm font-medium">{comm.subject}</span>}
                        </div>
                        <p className="text-sm text-gray-600">{comm.notes}</p>
                        <p className="text-xs text-gray-400 mt-2">
                          {new Date(comm.logged_at).toLocaleString()} • {comm.logged_by_name}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Student Messages Tab */}
        <TabsContent value="messages" className="mt-4">
          <Card data-testid="student-messages-panel">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm sm:text-base flex items-center gap-2">
                  <i className="fas fa-bell text-indigo-500"></i>
                  Updates
                </CardTitle>
                <button onClick={fetchStudentMessages} className="text-xs text-gray-400 hover:text-gray-600">
                  <i className="fas fa-sync-alt mr-1"></i>Refresh
                </button>
              </div>
              <CardDescription className="text-xs">Unified timeline — messages, document & payment requests, agreements and admission progress. Reply to the student below.</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Unified timeline */}
              <div className="border rounded-lg overflow-hidden">
                <div className="p-3 space-y-3 max-h-[440px] overflow-y-auto bg-gray-50" data-testid="admin-updates-list">
                  {staffUpdates.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <i className="fas fa-inbox text-2xl mb-2"></i>
                      <p className="text-xs">No updates yet.</p>
                    </div>
                  ) : (
                    staffUpdates.map(item => {
                      const fromStaff = item.sender === 'staff';
                      const isSystem = item.sender === 'system' || item.type === 'stage_change';
                      const ts = item.ts ? new Date(item.ts).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
                      const meta = item.meta || {};
                      if (isSystem) {
                        return (
                          <div key={item.id} className="flex justify-center" data-testid={`adm-update-${item.type}`}>
                            <div className="bg-indigo-50 text-indigo-700 text-[11px] font-medium rounded-full px-3 py-1 border border-indigo-100">
                              <i className="fas fa-graduation-cap mr-1.5"></i>{item.title}{ts ? ` · ${ts}` : ''}
                            </div>
                          </div>
                        );
                      }
                      const typeLabel = {
                        document_request: 'Document requested', document_uploaded: 'Document',
                        payment: 'Payment request', receipt: 'Payment receipt', agreement: 'Agreement',
                      }[item.type];
                      return (
                        <div key={item.id} className={`flex ${fromStaff ? 'justify-end' : 'justify-start'}`} data-testid={`adm-update-${item.type}`}>
                          <div className={`max-w-[80%] rounded-xl px-3 py-2 shadow-sm ${fromStaff ? 'bg-[#192650] text-white rounded-br-sm' : 'bg-white border text-gray-800 rounded-bl-sm'}`}>
                            <span className={`text-[9px] font-semibold ${fromStaff ? 'text-white/70' : 'text-indigo-600'}`}>{item.sender_name}</span>
                            {typeLabel && (
                              <p className={`text-[9px] font-semibold uppercase tracking-wide mt-0.5 ${fromStaff ? 'text-white/60' : 'text-[#192650]'}`}>{typeLabel}</p>
                            )}
                            {item.title && item.type !== 'message' && <p className="text-xs font-semibold leading-snug">{item.title}</p>}
                            {item.body && <p className="text-xs leading-relaxed mt-0.5 whitespace-pre-line">{item.body}</p>}
                            {item.type === 'payment' && meta.amount ? <p className="text-xs font-bold mt-1">₹{Number(meta.amount).toLocaleString('en-IN')} {meta.paid ? '· Paid ✓' : '· Due'}</p> : null}
                            {item.type === 'receipt' && meta.status ? <p className={`text-[10px] mt-1 ${fromStaff ? 'text-white/70' : 'text-amber-600'}`}>Status: {meta.status}</p> : null}
                            {item.type === 'agreement' ? <p className={`text-[10px] mt-1 ${fromStaff ? 'text-white/70' : (meta.signed ? 'text-emerald-600' : 'text-purple-600')}`}>{meta.signed ? 'Signed ✓' : 'Awaiting signature'}</p> : null}
                            <p className={`text-[8px] mt-1 ${fromStaff ? 'text-white/40' : 'text-gray-400'}`}>{ts}</p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Reply Input */}
                <div className="p-2 bg-white border-t flex gap-2">
                  <input
                    type="text"
                    value={studentMsgReply}
                    onChange={e => setStudentMsgReply(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && studentMsgReply.trim()) {
                        setSendingReply(true);
                        apiClient.post(`/student-portal/${portalId}/student-messages/reply`, { content: studentMsgReply })
                          .then(() => { setStudentMsgReply(''); fetchStudentMessages(); toast.success('Reply sent'); })
                          .catch(() => toast.error('Failed to send'))
                          .finally(() => setSendingReply(false));
                      }
                    }}
                    placeholder="Type your reply to the student..."
                    className="flex-1 border rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#192650]/20"
                    data-testid="admin-reply-input"
                  />
                  <button
                    onClick={() => {
                      if (!studentMsgReply.trim()) return;
                      setSendingReply(true);
                      apiClient.post(`/student-portal/${portalId}/student-messages/reply`, { content: studentMsgReply })
                        .then(() => { setStudentMsgReply(''); fetchStudentMessages(); toast.success('Reply sent'); })
                        .catch(() => toast.error('Failed to send'))
                        .finally(() => setSendingReply(false));
                    }}
                    disabled={sendingReply || !studentMsgReply.trim()}
                    className="bg-[#192650] text-white px-4 py-2 rounded-lg text-xs font-medium disabled:opacity-50"
                    data-testid="admin-send-reply-btn"
                  >
                    {sendingReply ? <i className="fas fa-spinner fa-spin"></i> : <><i className="fas fa-paper-plane mr-1"></i>Send</>}
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Upload Document Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>Upload a document for this student (PDF, JPG, PNG - max 10MB)</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Document Name *</Label>
              <Input 
                placeholder="e.g., Passport Copy, Offer Letter"
                value={uploadForm.document_name}
                onChange={(e) => setUploadForm({...uploadForm, document_name: e.target.value})}
              />
            </div>
            <div>
              <Label>File *</Label>
              <Input 
                type="file" 
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setSelectedFile(e.target.files[0])}
              />
            </div>
            <div>
              <Label>Description (Optional)</Label>
              <Textarea 
                placeholder="Add notes about this document..."
                value={uploadForm.description}
                onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
                rows={2}
              />
            </div>
            <label className="flex items-start gap-2.5 p-3 rounded-lg border border-indigo-200 bg-indigo-50/60 cursor-pointer" data-testid="share-with-student-toggle">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-[#192650]"
                checked={uploadForm.share_with_student}
                onChange={(e) => setUploadForm({...uploadForm, share_with_student: e.target.checked})}
                data-testid="share-with-student-checkbox"
              />
              <span className="text-xs text-gray-700">
                <span className="font-semibold"><i className="fas fa-share-nodes mr-1 text-indigo-600"></i>Share with student</span>
                <br />
                <span className="text-[11px] text-gray-500">Notify the student by email &amp; push (e.g. an offer letter). It will appear in their Updates and Documents, ready to download.</span>
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpload} disabled={uploading}>
              {uploading ? <><i className="fas fa-spinner fa-spin mr-2"></i> Uploading...</> : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Communication Dialog */}
      <Dialog open={commDialogOpen} onOpenChange={setCommDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log Communication</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Type *</Label>
              <Select value={commForm.communication_type} onValueChange={(v) => setCommForm({...commForm, communication_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COMMUNICATION_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Subject (Optional)</Label>
              <Input 
                placeholder="Brief subject"
                value={commForm.subject}
                onChange={(e) => setCommForm({...commForm, subject: e.target.value})}
              />
            </div>
            <div>
              <Label>Notes *</Label>
              <Textarea 
                placeholder="Describe the communication..."
                value={commForm.notes}
                onChange={(e) => setCommForm({...commForm, notes: e.target.value})}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCommDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddCommunication}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Stage Dialog */}
      <Dialog open={stageDialogOpen} onOpenChange={setStageDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {portal?.current_stage === 'visa_denied' && newStage === 'visa_application_submitted' 
                ? 'Resubmit Visa Application' 
                : 'Update Admission Stage'}
            </DialogTitle>
            <DialogDescription>
              {portal?.current_stage === 'visa_application_submitted' 
                ? 'Record the visa decision outcome'
                : portal?.current_stage === 'visa_denied' && newStage === 'visa_application_submitted'
                ? 'Move back to visa application stage for resubmission'
                : 'Move to the next stage in the admission process'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Special UI for Visa Decision */}
            {portal?.current_stage === 'visa_application_submitted' ? (
              <div>
                <Label className="mb-2 block">Visa Decision Outcome *</Label>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    type="button"
                    variant={newStage === 'visa_approved' ? 'default' : 'outline'}
                    className={`h-auto py-4 flex-col gap-1 ${newStage === 'visa_approved' ? 'bg-emerald-600 hover:bg-emerald-700' : 'border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300'}`}
                    onClick={() => setNewStage('visa_approved')}
                  >
                    <CheckCircle className={`w-6 h-6 ${newStage === 'visa_approved' ? 'text-white' : 'text-emerald-600'}`} />
                    <span className={`text-sm font-medium ${newStage === 'visa_approved' ? 'text-white' : 'text-emerald-700'}`}>Approved</span>
                  </Button>
                  <Button
                    type="button"
                    variant={newStage === 'visa_denied' ? 'default' : 'outline'}
                    className={`h-auto py-4 flex-col gap-1 ${newStage === 'visa_denied' ? 'bg-red-600 hover:bg-red-700' : 'border-red-200 hover:bg-red-50 hover:border-red-300'}`}
                    onClick={() => setNewStage('visa_denied')}
                  >
                    <Ban className={`w-6 h-6 ${newStage === 'visa_denied' ? 'text-white' : 'text-red-600'}`} />
                    <span className={`text-sm font-medium ${newStage === 'visa_denied' ? 'text-white' : 'text-red-700'}`}>Denied</span>
                  </Button>
                </div>
                {newStage === 'visa_denied' && (
                  <p className="text-xs text-gray-500 mt-2">
                    <i className="fas fa-info-circle mr-1"></i>
                    If denied, you can later resubmit the visa application
                  </p>
                )}
              </div>
            ) : (
              <div>
                <Label>New Stage *</Label>
                <Select value={newStage} onValueChange={setNewStage}>
                  <SelectTrigger><SelectValue placeholder="Select stage" /></SelectTrigger>
                  <SelectContent>
                    {BACKEND_STAGES.filter(s => {
                      // If at visa_denied, only allow going back to visa_application_submitted
                      if (portal?.current_stage === 'visa_denied') {
                        return s.value === 'visa_application_submitted';
                      }
                      // If at visa_approved, allow moving to ticket_travel_date
                      if (portal?.current_stage === 'visa_approved') {
                        return s.value === 'ticket_travel_date';
                      }
                      return true;
                    }).map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>
                {portal?.current_stage === 'visa_denied' && newStage === 'visa_application_submitted'
                  ? 'Reason for Resubmission *'
                  : 'Notes (Optional)'}
              </Label>
              <Textarea 
                placeholder={
                  portal?.current_stage === 'visa_denied' && newStage === 'visa_application_submitted'
                    ? 'Explain why the visa is being resubmitted...'
                    : 'Add notes about this stage change...'
                }
                value={stageNotes}
                onChange={(e) => setStageNotes(e.target.value)}
                required={portal?.current_stage === 'visa_denied' && newStage === 'visa_application_submitted'}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setStageDialogOpen(false); setNewStage(''); setStageNotes(''); }}>Cancel</Button>
            <Button 
              onClick={handleUpdateStage} 
              className={newStage === 'visa_denied' ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'}
              disabled={!newStage || (portal?.current_stage === 'visa_denied' && newStage === 'visa_application_submitted' && !stageNotes)}
            >
              {portal?.current_stage === 'visa_denied' && newStage === 'visa_application_submitted' 
                ? 'Confirm Resubmission'
                : newStage === 'visa_denied'
                ? 'Record Denial'
                : newStage === 'visa_approved'
                ? 'Record Approval'
                : 'Update Stage'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel/Defer Admission Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ban className="w-5 h-5 text-red-500" />
              Cancel or Defer Admission
            </DialogTitle>
            <DialogDescription>
              {portal?.full_name} - {portal?.student_id}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Reason */}
            <div>
              <Label className="text-sm">Reason (Optional)</Label>
              <Textarea
                placeholder="Enter reason for cancellation or deferral..."
                value={statusReason}
                onChange={(e) => setStatusReason(e.target.value)}
                rows={2}
                className="mt-1"
              />
            </div>

            {/* Defer to Intake */}
            <div>
              <Label className="text-sm">Defer to Intake</Label>
              <p className="text-xs text-gray-500 mb-1">Select if deferring to a specific intake</p>
              <Select value={deferToIntake} onValueChange={setDeferToIntake}>
                <SelectTrigger>
                  <SelectValue placeholder="Select intake (for defer only)" />
                </SelectTrigger>
                <SelectContent>
                  {activeIntakes
                    .filter(i => i.name !== portal?.intake) // Exclude current intake
                    .map(intake => (
                      <SelectItem key={intake.id || intake.name} value={intake.name}>
                        {intake.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-3 pt-2">
              <Button
                variant="outline"
                className="h-auto py-3 flex-col gap-1 border-red-200 hover:bg-red-50 hover:border-red-300 hover:text-red-700"
                onClick={() => handleStatusChange('cancelled')}
                data-testid="cancel-admission-btn"
              >
                <Ban className="w-5 h-5 text-red-500" />
                <span className="text-sm font-medium">Cancel</span>
                <span className="text-[10px] text-gray-500">Remove from admissions</span>
              </Button>
              <Button
                variant="outline"
                className="h-auto py-3 flex-col gap-1 border-amber-200 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700"
                onClick={() => handleStatusChange('deferred')}
                data-testid="defer-admission-btn"
              >
                <Pause className="w-5 h-5 text-amber-500" />
                <span className="text-sm font-medium">Defer</span>
                <span className="text-[10px] text-gray-500">Move to selected intake</span>
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setStatusDialogOpen(false); setStatusReason(''); setDeferToIntake(''); }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Raise Invoice Dialog */}
      <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <i className="fas fa-file-invoice text-indigo-600"></i>
              Raise Invoice
            </DialogTitle>
            <DialogDescription>
              Create a GST-compliant invoice for {portal?.full_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Student Info */}
            <div className="p-3 bg-indigo-50 rounded-lg">
              <p className="text-sm font-medium text-indigo-900">{portal?.full_name}</p>
              <p className="text-xs text-indigo-700">{portal?.student_id}</p>
              {portal?.university_name && (
                <p className="text-xs text-indigo-600 mt-1">University: {portal.university_name}</p>
              )}
            </div>

            {/* Payment Type */}
            <div>
              <Label>Invoice For *</Label>
              <Select 
                value={invoiceForm.payment_type_name} 
                onValueChange={(v) => setInvoiceForm({...invoiceForm, payment_type_name: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {invoiceForm.payment_type_name && ![
                    'Consultancy Fee','Registration Fee','Documentation Charge','Visa Processing Charge',
                    'Application Fee','Service Charge','Tuition Fee','Miscellaneous Charge'
                  ].includes(invoiceForm.payment_type_name) && (
                    <SelectItem value={invoiceForm.payment_type_name}>{invoiceForm.payment_type_name} (from schedule)</SelectItem>
                  )}
                  <SelectItem value="Consultancy Fee">Consultancy Fee</SelectItem>
                  <SelectItem value="Registration Fee">Registration Fee</SelectItem>
                  <SelectItem value="Documentation Charge">Documentation Charge</SelectItem>
                  <SelectItem value="Visa Processing Charge">Visa Processing Charge</SelectItem>
                  <SelectItem value="Application Fee">Application Fee</SelectItem>
                  <SelectItem value="Service Charge">Service Charge</SelectItem>
                  <SelectItem value="Tuition Fee">Tuition Fee</SelectItem>
                  <SelectItem value="Miscellaneous Charge">Miscellaneous Charge</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Amount */}
            <div>
              <Label>Amount (₹) *</Label>
              <Input
                type="number"
                placeholder="Enter invoice amount"
                value={invoiceForm.gross_amount}
                onChange={(e) => setInvoiceForm({...invoiceForm, gross_amount: e.target.value})}
                className="text-lg"
              />
            </div>

            {/* GST Type */}
            <div>
              <Label>GST Treatment</Label>
              <Select 
                value={invoiceForm.gst_type} 
                onValueChange={(v) => setInvoiceForm({...invoiceForm, gst_type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inclusive">GST Inclusive (18% included in amount)</SelectItem>
                  <SelectItem value="exclusive">GST Exclusive (18% added to amount)</SelectItem>
                  <SelectItem value="not_applicable">No GST</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                {invoiceForm.gst_type === 'inclusive' ? 
                  'GST amount will be calculated from the total' :
                  invoiceForm.gst_type === 'exclusive' ?
                  'GST will be added on top of the amount' :
                  'No GST will be applied'}
              </p>
            </div>

            {/* Description */}
            <div>
              <Label>Description (Optional)</Label>
              <Textarea
                placeholder="Add any notes or description for this invoice..."
                value={invoiceForm.description}
                onChange={(e) => setInvoiceForm({...invoiceForm, description: e.target.value})}
                rows={2}
              />
            </div>

            {/* Preview */}
            {invoiceForm.gross_amount && (
              <div className="p-3 bg-gray-50 rounded-lg border">
                <p className="text-xs font-medium text-gray-700 mb-2">Invoice Preview:</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-gray-600">Amount:</span>
                  <span className="font-medium text-right">₹{parseFloat(invoiceForm.gross_amount || 0).toLocaleString()}</span>
                  {invoiceForm.gst_type !== 'not_applicable' && (
                    <>
                      <span className="text-gray-600">GST (18%):</span>
                      <span className="text-right">
                        {invoiceForm.gst_type === 'inclusive' ? 
                          `Included` :
                          `+ ₹${(parseFloat(invoiceForm.gross_amount || 0) * 0.18).toLocaleString()}`}
                      </span>
                    </>
                  )}
                  <span className="text-gray-900 font-semibold pt-1 border-t">Total:</span>
                  <span className="text-indigo-700 font-bold text-right pt-1 border-t">
                    ₹{invoiceForm.gst_type === 'exclusive' ? 
                      (parseFloat(invoiceForm.gross_amount || 0) * 1.18).toLocaleString() :
                      parseFloat(invoiceForm.gross_amount || 0).toLocaleString()}
                  </span>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setInvoiceDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRaiseInvoice}
              disabled={invoiceLoading || !invoiceForm.gross_amount}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {invoiceLoading ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Creating...
                </>
              ) : (
                <>
                  <i className="fas fa-file-invoice mr-2"></i>
                  Raise Invoice
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unlock Profile Dialog (Admin Only) */}
      <Dialog open={unlockDialogOpen} onOpenChange={setUnlockDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-700">
              <Unlock className="w-5 h-5" />
              Unlock Student Profile
            </DialogTitle>
            <DialogDescription>
              This profile is locked because the student has completed the admission process (Student Flown).
              Unlocking allows further edits to the profile.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-center gap-2 text-orange-700">
                <i className="fas fa-exclamation-triangle"></i>
                <span className="font-medium text-sm">Admin Action Required</span>
              </div>
              <p className="text-xs text-orange-600 mt-1">
                Only administrators can unlock profiles. This action will be logged.
              </p>
            </div>
            <div>
              <Label>Reason for Unlocking *</Label>
              <Textarea
                placeholder="Explain why this profile needs to be unlocked..."
                value={unlockReason}
                onChange={(e) => setUnlockReason(e.target.value)}
                className="mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setUnlockDialogOpen(false); setUnlockReason(''); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleUnlockProfile}
              disabled={!unlockReason.trim()}
              className="bg-orange-600 hover:bg-orange-700"
            >
              <Unlock className="w-4 h-4 mr-2" />
              Unlock Profile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Admission Dialog (Admin Only, Cancelled Only) */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="w-5 h-5" />
              Delete Admission Record
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete this admission record?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 text-red-700">
                <i className="fas fa-exclamation-triangle"></i>
                <span className="font-medium text-sm">Warning: This action cannot be undone!</span>
              </div>
              <p className="text-xs text-red-600 mt-1">
                This will permanently delete the admission record for <strong>{portal?.full_name}</strong> ({portal?.student_id}), 
                including all documents, activities, and payment records associated with this admission.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteAdmission}
              disabled={deleteLoading}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              {deleteLoading ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Permanently
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Profile Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-indigo-600" />
              Edit Student Profile
            </DialogTitle>
            <DialogDescription>
              {portal?.full_name} - {portal?.student_id}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Personal Information Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-700 border-b pb-1">Personal Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Full Name *</Label>
                  <Input
                    value={editForm.full_name}
                    onChange={(e) => setEditForm({...editForm, full_name: e.target.value})}
                    placeholder="Student's full name"
                    data-testid="edit-full-name"
                  />
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                    placeholder="email@example.com"
                    data-testid="edit-email"
                  />
                </div>
                <div>
                  <Label className="text-xs">Phone</Label>
                  <Input
                    value={editForm.phone}
                    onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                    placeholder="Phone number"
                    data-testid="edit-phone"
                  />
                </div>
                <div>
                  <Label className="text-xs">WhatsApp Number</Label>
                  <Input
                    value={editForm.whatsapp_number}
                    onChange={(e) => setEditForm({...editForm, whatsapp_number: e.target.value})}
                    placeholder="WhatsApp number"
                    data-testid="edit-whatsapp"
                  />
                </div>
                <div>
                  <Label className="text-xs">Gender</Label>
                  <Select 
                    value={editForm.gender} 
                    onValueChange={(v) => setEditForm({...editForm, gender: v})}
                  >
                    <SelectTrigger data-testid="edit-gender">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Admission Details Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-700 border-b pb-1">Admission Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">University</Label>
                  <CreatableSelect
                    options={universities}
                    value={editForm.university_name}
                    onValueChange={(value) => setEditForm({...editForm, university_name: value})}
                    onCreateOption={handleAddUniversity}
                    placeholder="Select or add university"
                    createLabel="Add new university"
                    data-testid="edit-university"
                  />
                </div>
                <div>
                  <Label className="text-xs">Course/Program</Label>
                  <CreatableSelect
                    options={programs}
                    value={editForm.course}
                    onValueChange={(value) => setEditForm({...editForm, course: value})}
                    onCreateOption={handleAddProgram}
                    placeholder="Search or add program..."
                    searchPlaceholder="Type to search or add..."
                    createLabel="Add new program"
                    canCreate={canAddProgram}
                    data-testid="edit-course"
                  />
                </div>
                <div>
                  <Label className="text-xs">Intake Period</Label>
                  <Select 
                    value={editForm.intake} 
                    onValueChange={(v) => setEditForm({...editForm, intake: v})}
                  >
                    <SelectTrigger data-testid="edit-intake">
                      <SelectValue placeholder="Select intake" />
                    </SelectTrigger>
                    <SelectContent>
                      {activeIntakes.map((intake) => (
                        <SelectItem key={intake.id || intake.name} value={intake.name}>
                          <span className="flex items-center gap-2">
                            {intake.name}
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                              intake.status === 'Active' ? 'bg-green-100 text-green-700' :
                              intake.status === 'Upcoming' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>{intake.status || 'Unknown'}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Destination Country</Label>
                  <SearchableSelect
                    options={COUNTRIES}
                    value={editForm.destination_country}
                    onValueChange={(value) => setEditForm({...editForm, destination_country: value})}
                    placeholder="Search country..."
                    searchPlaceholder="Type to search countries..."
                    emptyMessage="No country found."
                    data-testid="edit-destination"
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setEditDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveProfile}
              disabled={editLoading || !editForm.full_name.trim()}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-profile-btn"
            >
              {editLoading ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-check mr-2"></i>
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Admission Date Edit Dialog (Admin Only) */}
      <Dialog open={admDateDialogOpen} onOpenChange={setAdmDateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-indigo-600" />
              Change Admission Date
            </DialogTitle>
            <DialogDescription>
              This date is used for monthly/weekly admission counts and bonus calculations. Changes are logged in the audit trail.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-medium">Current Date</Label>
              <p className="text-sm text-gray-600 mt-1">
                {portal?.created_at 
                  ? new Date(portal.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
                  : '-'}
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium" htmlFor="new-adm-date">New Date <span className="text-red-500">*</span></Label>
              <Input
                id="new-adm-date"
                type="date"
                value={admDateValue}
                onChange={(e) => setAdmDateValue(e.target.value)}
                className="mt-1"
                data-testid="admission-date-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium" htmlFor="adm-date-reason">Reason for Change <span className="text-red-500">*</span></Label>
              <Textarea
                id="adm-date-reason"
                placeholder="e.g., Counsellor converted to admission this month by mistake, actual conversion was last month"
                value={admDateReason}
                onChange={(e) => setAdmDateReason(e.target.value)}
                className="mt-1"
                rows={3}
                data-testid="admission-date-reason"
              />
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setAdmDateDialogOpen(false)} data-testid="cancel-adm-date-btn">
              Cancel
            </Button>
            <Button
              onClick={handleSaveAdmDate}
              disabled={admDateLoading || !admDateValue || !admDateReason.trim()}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-adm-date-btn"
            >
              {admDateLoading ? 'Saving...' : 'Update Date'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {staffPreview && (
        <div className="fixed inset-0 z-[70] bg-black/70 flex flex-col" data-testid="staff-document-preview-modal">
          <div className="flex items-center justify-between px-4 py-3 bg-[#192650] text-white">
            <div className="min-w-0 flex-1 mr-3">
              <p className="text-sm font-semibold truncate">{staffPreview.doc.document_name || staffPreview.doc.file_name || staffPreview.doc.original_filename || 'Document'}</p>
              <p className="text-[10px] text-white/70 truncate">{staffPreview.doc.uploaded_by === 'student' ? 'Student upload' : 'Staff upload'}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button size="sm" variant="outline" className="h-8 text-xs bg-white/15 text-white border-white/20 hover:bg-white/25" onClick={() => handleDownload(staffPreview.doc.id, staffPreview.doc.file_name || staffPreview.doc.original_filename || 'document')} data-testid="staff-preview-download-btn">
                <i className="fas fa-download mr-1"></i>Download
              </Button>
              <Button size="sm" variant="outline" className="h-8 text-xs bg-white/15 text-white border-white/20 hover:bg-white/25" onClick={closeStaffPreview} data-testid="staff-preview-close-btn">
                <i className="fas fa-times mr-1"></i>Close
              </Button>
            </div>
          </div>
          <div className="flex-1 overflow-auto flex items-center justify-center p-2 sm:p-4">
            {staffPreview.loading ? (
              <div className="text-white text-center"><i className="fas fa-spinner fa-spin text-2xl mb-2"></i><p className="text-sm">Loading document…</p></div>
            ) : staffPreview.kind === 'pdf' ? (
              <iframe src={staffPreview.url} title="Document preview" className="w-full h-full bg-white rounded" data-testid="staff-preview-pdf-frame"></iframe>
            ) : (
              <img src={staffPreview.url} alt="Document preview" className="max-w-full max-h-full object-contain rounded shadow-lg" data-testid="staff-preview-image" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
